import { H5 } from "@sellar-io/components";
import { SupplierStorefrontV2GetResponseType } from "@sellar-io/api-v2/types";
import { ContextOrderBasketType } from "../../../../../../../hooks";
import {
  formatCurrency,
  getBadgeDisplayName,
  getProductDisplayNameByKey,
  pluralize,
} from "@sellar-io/common";
import { OrderSummaryPrice } from "../../../../../../../components";
import { EstimatedDateDraftBasket } from "./EstimatedDateDraftBasket";
import { ProductItemImage } from "./ProductItemImage";
import { DietaryDisplayList } from "../../../../../../../components/dietaryDisplayList/DietaryDisplayList";

import { isPromotionalOrderItem } from "../../../../../../stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { BadgePurple } from "../../../../../../../components/badge/Badge";

const ProductItemDetails = (props: {
  product: {
    parentName: string;
    vegan: boolean;
    glutenFree: boolean;
    lactoseFree: boolean;
    packQuantity: number | null;
    volume: number | null;
    unit: string | null;
    containerType: string | null;
  };
}) => {
  const product = props.product;

  const productContainerDisplayName = product.containerType
    ? pluralize(
        getProductDisplayNameByKey(product.containerType) ?? "",
        product.packQuantity ?? 1,
      )
    : null;

  return (
    <div className="flex flex-col">
      <div className="flex gap-2">
        <H5>{product.parentName}</H5>{" "}
        <div>
          <DietaryDisplayList
            dietaryRequirements={{
              VG: Boolean(product.vegan),
              GF: Boolean(product.glutenFree),
              LF: Boolean(product.lactoseFree),
            }}
            mode="text"
          />
        </div>
      </div>

      <div>
        {product.packQuantity && product.volume && product.unit && (
          <>
            {product.packQuantity}x {product.volume}
            {product.unit}{" "}
          </>
        )}
        <>{productContainerDisplayName}</>
      </div>
    </div>
  );
};

export const OrderItemDetailsList = ({
  deliveryArea,
  fulfillmentType,
  orderItemList,
  delivery,
  subTotal,
  discount,
  tax,
  total,
}: {
  deliveryArea?: SupplierStorefrontV2GetResponseType["DeliveryArea"];
  fulfillmentType: SupplierStorefrontV2GetResponseType["FulfillmentType"]["fulfillmentType"];
  orderItemList: NonNullable<ContextOrderBasketType>["OrderItems"];
  delivery: string;
  subTotal: string;
  discount:
    | {
        calculatedTotal: string;
        value: string;
      }
    | undefined;

  tax: {
    name: string;
    percentage: string;
    total: string;
  };
  total: string;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="divide-y divide-solid">
        {orderItemList?.map((orderItem, index) => {
          const product = orderItem.Product;
          return (
            <div
              key={orderItem.id ?? index}
              className="flex items-center justify-between gap-2 py-4 md:px-3"
            >
              <div className="flex grow basis-0 items-center gap-4">
                <div className="min-w-5 font-semibold text-zinc-800">
                  {orderItem.quantity}x
                </div>

                {/* order item image */}
                <ProductItemImage
                  imageUrl={product.Parent?.heroImageUrl ?? null}
                />

                {product.type === "productItem" ? (
                  <ProductItemDetails
                    product={{
                      ...product,
                      parentName: product.Parent?.name ?? "",
                      glutenFree: !!product.glutenFree,
                      lactoseFree: !!product.lactoseFree,
                      vegan: !!product.vegan,
                      packQuantity: product.packQuantity ?? null,
                      volume: product.volume ?? null,
                      unit: product.unit ?? null,
                      containerType: product.containerType ?? null,
                    }}
                  />
                ) : (
                  getBadgeDisplayName(product.shape ?? "")
                )}
              </div>

              {/* order item price */}

              <div className="flex flex-wrap items-center justify-end gap-2">
                {isPromotionalOrderItem(orderItem) && (
                  <BadgePurple icon="PARTY_POPPER" />
                )}
                {orderItem.fullPrice !== orderItem.price && (
                  <div className="text-sm text-zinc-500 line-through">
                    {formatCurrency(orderItem.fullPrice)}
                  </div>
                )}
                <div className="font-semibold text-zinc-800">
                  {formatCurrency(orderItem.price)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <OrderSummaryPrice
        delivery={delivery}
        subTotal={subTotal}
        discount={discount}
        tax={tax}
        total={total}
      />

      <EstimatedDateDraftBasket
        deliveryArea={deliveryArea}
        fulfillmentType={fulfillmentType}
      />
    </div>
  );
};
